import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Upload, Button, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { ProjectsService } from '../api/services/projects.service';
import { TasksService } from '../api/services/tasks.service';

const fileTypeOptions = [
  { value: 'document', label: 'Документ' },
  { value: 'image', label: 'Изображение' },
  { value: 'video', label: 'Видео' },
  { value: 'audio', label: 'Аудио' },
  { value: 'archive', label: 'Архив' },
  { value: 'other', label: 'Другое' },
];

export default function UniversalFileUploadModal({ open, onCancel, onUpload, defaultTitle = '', defaultTaskId = null, defaultProjectId = null }) {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      ProjectsService.getProjects().then((res) => {
        setProjects(res.data || []);
      }).catch(() => {});
      TasksService.getTasks().then((res) => {
        setTasks(res.data || []);
      }).catch(() => {});
      
      const initialValues = {};
      if (defaultTitle) initialValues.title = defaultTitle;
      if (defaultTaskId) initialValues.task_id = defaultTaskId;
      if (defaultProjectId) initialValues.project_id = defaultProjectId;
      form.setFieldsValue(initialValues);
    }
  }, [open, defaultTitle, defaultTaskId, defaultProjectId, form]);

  const handleFinish = async (values) => {
    if (fileList.length === 0) {
      message.error("Пожалуйста, прикрепите файл");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      fileList.forEach(file => {
        formData.append('file', file);
      });
      
      await onUpload({
        file: formData,
        title: values.title,
        fileType: values.file_type,
        projectId: values.project_id,
        taskId: values.task_id,
        description: values.description
      });
      
      form.resetFields();
      setFileList([]);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onCancel();
  };

  return (
    <Modal
      title="Загрузка файла"
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>Отмена</Button>,
        <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()}>Загрузить</Button>
      ]}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item name="file" label="Файл(ы)" required>
          <Upload
            beforeUpload={(file) => {
              setFileList(prev => [...prev, file]);
              if (!form.getFieldValue('title')) {
                form.setFieldsValue({ title: file.name });
              }
              return false; // Prevent auto upload
            }}
            onRemove={(file) => setFileList(prev => prev.filter(f => f.uid !== file.uid))}
            fileList={fileList}
            multiple={true}
          >
            <Button icon={<UploadOutlined />}>Выберите файлы</Button>
          </Upload>
        </Form.Item>

        <Form.Item name="title" label="Название" rules={[{ required: true, message: 'Введите название файла' }]}>
          <Input placeholder="Название файла" />
        </Form.Item>

        <Form.Item name="file_type" label="Тип файла" rules={[{ required: true, message: 'Выберите тип файла' }]}>
          <Select options={fileTypeOptions} placeholder="Выберите тип" />
        </Form.Item>

        <Form.Item name="project_id" label="Проект (необязательно)">
          <Select
            allowClear
            placeholder="Привязать к проекту"
            options={projects.map(p => ({ value: p.id, label: p.name }))}
            showSearch
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>

        <Form.Item name="task_id" label="Задача (необязательно)">
          <Select
            allowClear
            placeholder="Привязать к задаче"
            options={tasks.map(t => ({ value: t.id, label: t.title }))}
            showSearch
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>

        <Form.Item name="description" label="Описание">
          <Input.TextArea rows={3} placeholder="Описание файла..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}
